import { FieldGroupsManager } from "./utils/fieldVisibilityManager";
import { initSearchableSelects } from "./utils/searchableSelect";

class FormFlowManager {
  private form: HTMLFormElement | null = null;
  private sendHistory: FormData[] = [];
  private prevButton: HTMLButtonElement | null = null;
  private fieldGroupsManager: FieldGroupsManager | null = null;
  private backFillData: FormData | null = null;

  constructor() {
    this.init();
  }

  init() {
    this.form = document.querySelector(".wp-form-flow-form");
    if (this.form) {
      this.form.addEventListener("submit", (e) => this.handleSubmit(e));
      this.processForm(new FormData(this.form));
      this.prevButton = this.form.querySelector(".wp-form-flow-prev-btn");
      this.managePrevButton();
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();

    if (this.fieldGroupsManager && !this.fieldGroupsManager.isValid()) {
      return;
    }

    const formData = new FormData(e.target as HTMLFormElement);
    this.processForm(formData);
  }

  goBack() {
    if (this.sendHistory.length > 1) {
      this.backFillData = this.sendHistory.pop() ?? null;
      const formData = this.sendHistory[this.sendHistory.length - 1];
      if (formData) {
        this.processForm(formData, true);
      }
    }
  }

  managePrevButton() {
    if (this.prevButton) {
      this.prevButton.addEventListener("click", () => this.goBack());
    }
  }

  async processForm(data: FormData, isBack: boolean = false): Promise<void> {
    const submitButton: HTMLButtonElement | null =
      this.form?.querySelector('button[type="submit"]') ??
      (null as HTMLButtonElement | null);
    submitButton?.setAttribute("disabled", "true");
    this.prevButton?.setAttribute("disabled", "true");
    return fetch(this.form?.getAttribute("action") || "", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-WP-Nonce": data.get("_nonce")?.toString() || "",
      },
      credentials: "include",
      body: JSON.stringify({
        _nonce: data.get("_nonce")?.toString() || "",
        values: Object.fromEntries(data.entries()),
      }),
    })
      .then((result) => {
        return result.json();
      })
      .then((response) => {
        // Handle guard messages and capture signed tokens for next submission
        if (response.guards && Array.isArray(response.guards)) {
          let blocked = false;
          for (const guard of response.guards) {
            // If guard is configured with noCache, remove any stale hidden tokens
            if (this.form && guard.noCache) {
              const stalePayload = this.form.querySelector(
                `input[name="__gpl[${guard.id}]"]`
              );
              const staleSig = this.form.querySelector(
                `input[name="__gsig[${guard.id}]"]`
              );
              stalePayload?.parentElement?.removeChild(stalePayload);
              staleSig?.parentElement?.removeChild(staleSig);
            }
            // Persist signed token as hidden inputs if we can pass (or even if blocked to redisplay message w/out rerun?)
            if (this.form && guard.payload && guard.signature) {
              // Avoid duplicating existing inputs
              const existingPayload = this.form.querySelector(
                `input[name="__gpl[${guard.id}]"]`
              ) as HTMLInputElement | null;
              const existingSig = this.form.querySelector(
                `input[name="__gsig[${guard.id}]"]`
              ) as HTMLInputElement | null;
              if (!existingPayload) {
                const pl = document.createElement("input");
                pl.type = "hidden";
                pl.name = `__gpl[${guard.id}]`;
                pl.value = guard.payload;
                this.form.appendChild(pl);
              }
              if (!existingSig) {
                const sg = document.createElement("input");
                sg.type = "hidden";
                sg.name = `__gsig[${guard.id}]`;
                sg.value = guard.signature;
                this.form.appendChild(sg);
              }
            }
            if (guard.message) {
              let messageContainer = this.form?.querySelector(
                ".wp-form-flow-guard-message"
              ) as HTMLElement;
              if (!messageContainer) {
                messageContainer = document.createElement("div");
                messageContainer.className = "wp-form-flow-guard-message";
                this.form
                  ?.querySelector(".wp-form-flow-form-content")
                  ?.prepend(messageContainer);
              }
              messageContainer.innerHTML = guard.message;
              messageContainer.style.display = "block";
              if (!guard.can_pass && !guard.redirect) {
                messageContainer.scrollIntoView({ behavior: "smooth", block: "center" });
              }
            }
            if (guard.redirect) {
              window.location.href = guard.redirect;
              return;
            }
            if (!guard.can_pass) {
              blocked = true;
              // Don't return yet; show remaining guard messages if any
            }
          }
          if (blocked) {
            return; // Stop progression if any guard blocks
          }
        }

        if (response.end) {
          // Trigger an event for end node reached
          const event = new CustomEvent("wpFormFlowEndReached", {
            detail: response.end,
          });
          window.dispatchEvent(event);
        }

        if (response.html) {
          const contentElement = this.form?.querySelector(
            ".wp-form-flow-form-content"
          );
          if (contentElement) {
            contentElement.innerHTML = response.html;

            // Re-populate fields with previously submitted data when going back
            if (isBack && this.backFillData) {
              const fillSource = this.backFillData;
              const seen = new Set<string>();
              for (const [name] of fillSource.entries()) {
                if (name === "_nonce" || seen.has(name)) continue;
                seen.add(name);
                const values: string[] = [];
                for (const [n, v] of fillSource.entries()) {
                  if (n === name) values.push(v as string);
                }
                const fields = contentElement.querySelectorAll<
                  HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
                >(`[name="${CSS.escape(name)}"]`);
                for (const field of fields) {
                  if (field instanceof HTMLInputElement) {
                    if (
                      field.type === "checkbox" ||
                      field.type === "radio"
                    ) {
                      field.checked = values.includes(field.value);
                    } else {
                      field.value = values[0] ?? "";
                    }
                  } else {
                    field.value = values[0] ?? "";
                  }
                }
              }
            }

            initSearchableSelects(contentElement);

            if (response.nodes && response.edges) {
              this.fieldGroupsManager = new FieldGroupsManager(
                submitButton ?? null,
                response.nodes,
                response.edges
              );
            }
          }

          if (response.price) {
            // hide prev
            this.prevButton?.setAttribute("hidden", "true");
          }

          if (response.redirect) {
            window.location.href = response.redirect;
          }

          if (!isBack) {
            this.sendHistory.push(data);
          }

          if (this.sendHistory.length > 1) {
            this.prevButton?.removeAttribute("hidden");
          } else {
            this.prevButton?.setAttribute("hidden", "true");
          }

          // Scroll to top of form
          this.form?.scrollIntoView({ behavior: "smooth" });
        }

        if (response.submitLabel && submitButton) {
          submitButton.textContent = response.submitLabel;
        }
      })
      .finally(() => {
        this.prevButton?.removeAttribute("disabled");
        if (this.fieldGroupsManager) {
          this.fieldGroupsManager.updateSubmitButtonState();
        } else {
          submitButton?.removeAttribute("disabled");
        }
      });
  }
}

export default FormFlowManager;
